import { IDiscipline } from 'api/account/business/dtos';
import { ILesson } from 'api/event/lesson/dtos';
import { PaymentMethod } from 'api/payment/dtos';
import {
  BaseEntity,
  BusinessQuery,
  CurrencyName,
  DateRangeQuery,
  Money,
  PaginationQuery,
} from 'api/shared';
import { LessonProduct } from 'api/shop/products/dtos';

export interface ListEmployeeQuery extends BusinessQuery, PaginationQuery {
  name?: string;
  type?: EmployeeTypeEnum;
  seasonIds?: number[];
  availableDate?: string;
}

interface PersonalRequest {
  firstName: string;
  lastName: string;
  phone?: string;
  gender?: GenderEnum;
  dateOfBirth?: string | null;
  address?: string;
  city?: string;
  postal?: string;
  country?: string;
}

interface ContactRequest extends PersonalRequest {
  email?: string;
}

interface CertificateRequest {
  discipline: string;
  name: string;
  id: string;
}

interface EmployeeRequest {
  contact?: ContactRequest;
  certificates?: CertificateRequest[];
  languages?: string[];
  notes?: string;
  disciplines?: { id: number }[];
  seasons?: { id: number }[];
}

export interface CreateEmployeeQuery extends EmployeeRequest, BusinessQuery {
  email: string;
  personal: PersonalRequest;
  type: EmployeeTypeEnum;
}
export interface UpdateEmployeeQuery extends Omit<CreateEmployeeQuery, 'type'> {
  id: number;
}

export interface UpdateEmployeeTypeQuery extends BusinessQuery {
  type: EmployeeTypeEnum;
  id: number;
}

export interface ListBalanceEntriesQuery extends BusinessQuery {
  startAt?: string;
  endAt?: string;
  employeeId: number;
  type?: BalanceEntryTypeEnum;
}

export interface GetEmployeeQuery extends BusinessQuery, DateRangeQuery {
  id: number;
}

export interface UpdateEmployeeSeasonsQuery extends BusinessQuery {
  id: number;
  seasons: { id: number }[];
}

export interface GetBalanceQuery extends BusinessQuery, DateRangeQuery {
  employeeId: number;
}

export interface CreateBalanceEntryQuery extends BusinessQuery {
  amount: Money;
  description?: string;
  paidAt?: string;
  employeeId: number;
  currency: CurrencyName;
}

export interface UpdateBalanceEntryQuery extends Partial<CreateBalanceEntryQuery> {
  id: number;
}

export interface DeleteEmployeeQuery extends BusinessQuery {
  id: number;
}

export interface RestoreEmployeeQuery extends BusinessQuery {
  id: number;
}

export interface CreateEmployeePayoutRequest extends BusinessQuery {
  employeeId: number;
  amount: Money;
  paidAt?: string;
  currency: string;
  method: PaymentMethod;
  description?: string;
}

export interface UpdateEmployeePayoutRequest extends Partial<CreateBalanceEntryQuery> {
  id: number;
  employeeId: number;
}

export interface ListEmployeePayoutsQuery extends BusinessQuery {
  employeeId: number;
}

export interface ListPaySourcesQuery extends BusinessQuery {
  employeeId: string | number;
}

export interface DeletePaySourcesQuery extends BusinessQuery {
  employeeId: string | number;
  id: string | number;
}

export interface CreatePaySourceQuery extends BusinessQuery {
  type: PaySourceTypeEnum;
  productIds?: number[];
  disciplineIds?: number[];
  hourlyRate?: Money;
  commissionRate?: number;
  periodLength?: number;
  periodUnit?: PeriodUnitEnum;
  recurringAmount?: Money;
  startAt?: string | null;
  endAt?: string | null;
  currency?: CurrencyName;
}

export interface UpdatePaySourceQuery extends Partial<CreatePaySourceQuery> {
  id: string | number;
}

export type BalanceEntry = LessonBalanceEntry;

export interface BaseBalanceEntry extends BaseEntity {
  amount: Money;
  description: string;
  employee: IInstructor;
  paidAt: Date;
  currency: CurrencyName;
}

export interface LessonBalanceEntry extends BaseBalanceEntry {
  lesson?: ILesson;
  details: BalanceEntryDetails;
  pendingDetails: BalanceEntryDetails;
  pendingAmount: Money;
  type: BalanceEntryTypeEnum.LESSON;
}

export interface BalanceEntryDetails {
  sources: {
    sourceId: number;
    amount: Money;
    currency: CurrencyName;
    commissionRate?: number;
    hourlyRate?: number;
    recurringAmount?: number;
  }[];
}

export interface RecurringBalanceEntry {
  paySource: RecurringPaySource;
  type: BalanceEntryTypeEnum.RECURRING;
}

export enum BalanceEntryTypeEnum {
  MANUAL = 'ManualBalanceEntry',
  RECURRING = 'RecurringBalanceEntry',
  LESSON = 'LessonBalanceEntry',
  RENTAL = 'RentalBalanceEntry',
}

export interface IInstructor extends BaseEntity {
  id: number;
  email: string;
  personal: Personal;
  contact?: Contact;
  languages?: string[];
  notes?: string;
  disciplines?: Pick<IDiscipline, 'id' | 'name' | 'color'>[];
  certificates?: Certificate[];
  type: EmployeeTypeEnum;
  seasons?: { id: number; name: string }[];
  lessonStats: EmployeeLessonStats[];
  availability: Pick<Availability, 'id' | 'startAt' | 'endAt' | 'isAvailable'>[];
  isAvailable: boolean;
}

export interface EmployeeLessonStats {
  lessonTotalMinutes: number;
  lessonCount: number;
  productId: number;
  productName: string;
}

export enum EmployeeTypeEnum {
  BASIC = 'Employee',
  INSTRUCTOR = 'Instructor',
}

export interface Certificate {
  id: string;
  name: string;
  file: string;
  discipline: string;
}

export interface Balance {
  total: number;
  paidOut?: number;
  currency: CurrencyName;
}

export interface DetailedBalance extends Balance {
  deduction: number;
  bonus: number;
  recurring: number;
  currency: CurrencyName;
  lessons?: DetailedBalanceLesson[];
}

export interface DetailedBalanceLesson {
  productId: number;
  totalEarned: number;
  pendingEarnings: number;
  totalMinutes: number;
}

export interface Personal {
  firstName: string;
  lastName: string;
  number?: string;
  prefix?: string;
  gender?: GenderEnum;
  dateOfBirth?: string;
}

export interface Contact extends Personal {
  email?: string;
}

export enum GenderEnum {
  MALE = 'M',
  FEMALE = 'F',
}

export interface PaySourceBase extends BaseEntity {
  type: PaySourceTypeEnum;
  employee: IInstructor;
  products?: LessonProduct[];
  disciplines?: IDiscipline[];
  startAt?: string;
  endAt?: string;
  currency: CurrencyName;
}

export interface RecurringPaySource extends PaySourceBase {
  type: PaySourceTypeEnum.RECURRING;
  periodLength?: number;
  periodUnit?: PeriodUnitEnum;
  recurringAmount: number;
}

export interface HourlyPaySource extends PaySourceBase {
  type: PaySourceTypeEnum.HOURLY;
  hourlyRate?: number;
}

export interface CommissionPaySource extends PaySourceBase {
  type: PaySourceTypeEnum.COMMISSION;
  commissionRate?: number;
}

export type PaySource = RecurringPaySource | HourlyPaySource | CommissionPaySource;

export enum PaySourceTypeEnum {
  HOURLY = 'HourlyPaySource',
  RECURRING = 'RecurringPaySource',
  COMMISSION = 'CommissionPaySource',
}
export enum PeriodUnitEnum {
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
}

//////// HOLIDAYS
export interface Availability {
  startAt: string;
  endAt: string;
  isAvailable: boolean;
  id: number;
  employee: Pick<IInstructor, 'id' | 'personal'>;
}

export interface GetAvailabilityQuery extends BusinessQuery {
  employeeId: number;
}

export interface CreateAvailabilityQuery extends BusinessQuery {
  employeeId: number;
  startAt: string;
  endAt: string;
  isAvailable: boolean;
}

export interface UpdateAvailabilityQuery extends Partial<CreateAvailabilityQuery> {
  employeeId: number;
  id: number;
}

export interface DeleteAvailabilityQuery extends BusinessQuery {
  employeeId: number;
  id: number;
}
